import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { apiClient, ApiError } from "@/api/client";
import type { HomeResponse, SubjectProgress } from "@/api/types";

/**
 * Shared `/me/home` cache (PRD §6.1 PE-1.1).
 *
 * The sidebar, Today and the Add-subject slide-over all need the same
 * enrolled-subject list. Fetching it per-component would mean the sidebar
 * silently disagreeing with the page next to it after an enrolment, so the
 * response lives here once and every surface reads from it.
 *
 * `load()` is fetch-once so mounting the shell on every navigation costs one
 * request per session; enrolment calls `refresh()` to force a refetch.
 */
export const useHomeStore = defineStore("home", () => {
  const data = ref<HomeResponse | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  let inflight: Promise<void> | null = null;

  const subjects = computed<SubjectProgress[]>(
    () => data.value?.enrolled_subjects ?? [],
  );
  const loaded = computed(() => data.value !== null);

  function fetchOnce(): Promise<void> {
    if (!inflight) {
      inflight = (async () => {
        loading.value = true;
        error.value = null;
        try {
          data.value = await apiClient.getHome();
        } catch (e) {
          error.value =
            e instanceof ApiError
              ? `API error ${e.status}`
              : "Failed to load your subjects";
        } finally {
          loading.value = false;
        }
      })().finally(() => {
        inflight = null;
      });
    }
    return inflight;
  }

  /** Fetch unless already loaded. Concurrent callers share one request. */
  async function load(): Promise<void> {
    if (data.value !== null) return;
    await fetchOnce();
  }

  /** Force a refetch — call after enrolling so the sidebar updates in place. */
  async function refresh(): Promise<void> {
    await fetchOnce();
  }

  /** Drop cached state on logout so the next user never sees the last one's subjects. */
  function reset(): void {
    data.value = null;
    error.value = null;
  }

  return { data, subjects, loaded, loading, error, load, refresh, reset };
});
